import React from 'react';
import { toast } from 'sonner';
import { Button, Input, Modal } from '@/components/ui';
import { createAdminPasswordUser } from '@/lib/auth/client-auth-service';
import { normalizeUsername, validateUsername } from '@/lib/auth/username';

interface AdminCreateUserModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function AdminCreateUserModal({ isOpen, onClose }: AdminCreateUserModalProps) {
  const [displayName, setDisplayName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [showPassword, setShowPassword] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);

  React.useEffect(() => {
    if (isOpen) {
      setDisplayName('');
      setEmail('');
      setPassword('');
      setShowPassword(false);
      setSubmitting(false);
    }
  }, [isOpen]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    const trimmedName = displayName.trim();
    const trimmedEmail = email.trim();

    if (!trimmedName || !trimmedEmail) {
      toast.error('Display name and email are required.');
      return;
    }

    const emailValidation = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailValidation.test(trimmedEmail)) {
      toast.error('Please enter a valid email address.');
      return;
    }

    const usernameValidation = validateUsername(trimmedName);
    if (!usernameValidation.ok) {
      if (usernameValidation.reason === 'length') {
        toast.error('Display name is too short to generate a valid username (minimum 3 characters).');
      } else {
        toast.error('Display name uses a reserved username.');
      }
      return;
    }

    setSubmitting(true);

    try {
      const body: Record<string, string> = {
        displayName: trimmedName,
        email: trimmedEmail,
      };

      if (password.trim()) {
        body.username = normalizeUsername(trimmedName);
        body.temporaryPassword = password.trim();
      } else {
        body.mode = 'federated';
      }

      await createAdminPasswordUser(body);
      toast.success(password.trim() ? 'User created with password.' : 'User created. They can sign in with Google using this email.');
      onClose();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to create user.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Add New User">
      <form noValidate onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Display Name"
          type="text"
          value={displayName}
          onChange={(event) => setDisplayName(event.target.value)}
          placeholder="e.g. Jane Smith"
          disabled={submitting}
          autoFocus
        />
        <Input
          label="Email"
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          placeholder="jane@example.com"
          disabled={submitting}
        />
        <Input
          label="Password (optional)"
          type={showPassword ? 'text' : 'password'}
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          placeholder="Leave blank for Google sign-in only"
          disabled={submitting}
        />
        <label className="flex items-center gap-2 text-sm font-medium text-aura-text/70">
          <input
            type="checkbox"
            checked={showPassword}
            onChange={(event) => setShowPassword(event.target.checked)}
            disabled={submitting}
            className="disabled:opacity-50"
          />
          Show password
        </label>

        <div className="flex gap-3 pt-2">
          <Button
            type="button"
            variant="ghost"
            onClick={onClose}
            disabled={submitting}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={submitting}
            className="flex-1 bg-[#006855] border-[#006855] hover:bg-[#005b4a] hover:border-[#005b4a]"
          >
            {submitting ? 'Creating...' : 'Create User'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
